import { useEffect, useRef, useState } from "react";
import { MdPlayArrow, MdPause } from "react-icons/md";

const LOGOS = [
  `${process.env.PUBLIC_URL}/clients/barc.png`,
  `${process.env.PUBLIC_URL}/clients/npcil.png`,
  `${process.env.PUBLIC_URL}/clients/aai.png`,
  `${process.env.PUBLIC_URL}/clients/ironMountain.png`,
  `${process.env.PUBLIC_URL}/clients/nhai.png`,
  `${process.env.PUBLIC_URL}/clients/dlf.jpg`,
  `${process.env.PUBLIC_URL}/clients/pgi.png`,
  `${process.env.PUBLIC_URL}/clients/reliance.png`,
];

const DURATION = 1500;
const EASING = "cubic-bezier(0.25, 0.1, 0.25, 1)";
const CONTROL_HEIGHT = 44;
const LOADER_WIDTH = 246;

export default function PrestigiousClients() {
  const [index, setIndex] = useState(0);
  const [state, setState] = useState("paused");
  const [hasBeenSeen, setHasBeenSeen] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(false);

  const timerRef = useRef(null);
  const containerRef = useRef(null);
  const touchStartX = useRef(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasBeenSeen) {
          setHasBeenSeen(true);
          setTimeout(() => {
            setShowLoader(true);
            setState("playing");
            setTimeout(() => {
              setShowPlayButton(true);
            }, 500);
          }, 1500);
        }
      },
      { threshold: 0.6 }
    );

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [hasBeenSeen]);

  useEffect(() => {
    if (state !== "playing") return;

    timerRef.current = setTimeout(() => {
      setIndex((prev) => (prev === LOGOS.length - 1 ? 0 : prev + 1));
    }, DURATION);

    return () => clearTimeout(timerRef.current);
  }, [index, state]);

  const togglePlay = () =>
    setState((s) => (s === "playing" ? "paused" : "playing"));

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) < 40) return;

    setIndex((prev) =>
      delta < 0
        ? Math.min(prev + 1, LOGOS.length - 1)
        : Math.max(prev - 1, 0)
    );
    setState("playing");
  };

  const getLogoStyle = (i) => {
    const diff = i - index;
    
    if (diff === 0) {
      return {
        opacity: 1,
        scale: 1,
        translateX: "0%",
        zIndex: 30,
        filter: "blur(0px)",
      };
    } else if (diff === -1 || diff === 1) {
      return {
        opacity: 0.3,
        scale: 0.65,
        translateX: diff === -1 ? "-110%" : "110%",
        zIndex: 20,
        filter: "blur(0px)",
      };
    } else if (diff === -2 || diff === 2) {
      return {
        opacity: 0.15,
        scale: 0.5,
        translateX: diff === -2 ? "-220%" : "220%",
        zIndex: 10,
        filter: "blur(1px)",
      };
    } else {
      return {
        opacity: 0,
        scale: 0.4,
        translateX: diff < 0 ? "-300%" : "300%",
        zIndex: 0,
        filter: "blur(2px)",
      };
    }
  };

  return (
    <div
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="relative mx-auto max-w-6xl select-none px-4 overflow-hidden"
    >
      {/* Heading */}
      <h2 className="text-center text-5xl font-bold tracking-tight text-orange-500 leading-tight py-4">
        Trusted by Industry Leaders
      </h2>

      {/* Logos */}
      <div className="relative h-[28rem] overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          {LOGOS.map((src, i) => {
            const style = getLogoStyle(i);
            return (
              <div
                key={i}
                className="absolute flex items-center justify-center transition-all duration-700"
                style={{
                  opacity: style.opacity,
                  transform: `translateX(${style.translateX}) scale(${style.scale})`,
                  zIndex: style.zIndex,
                  filter: style.filter,
                  transitionTimingFunction: EASING,
                  pointerEvents: i === index ? "auto" : "none",
                  width: "400px",
                }}
              >
                <img
                  src={src}
                  alt={`Client logo ${i + 1}`}
                  className="max-h-64 max-w-[380px] object-contain"
                  draggable={false}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Controller */}
      <div
        className={`
          mt-4 flex items-center justify-center gap-3
          pb-8
          transition-opacity duration-500
          ${hasBeenSeen ? "opacity-100" : "opacity-0 pointer-events-none"}
        `}
      >
        {/* Container for ball animation and loader - ensures no layout shift */}
        <div 
          className="relative flex items-center justify-center"
          style={{
            height: CONTROL_HEIGHT,
            minWidth: LOADER_WIDTH,
          }}
        >
          {/* Animated Ball that becomes the Loader */}
          <div
            className="absolute rounded-full backdrop-blur-md overflow-hidden"
            style={{
              height: CONTROL_HEIGHT,
              width: CONTROL_HEIGHT,
              background: "rgba(28,28,30,0.85)",
              animation: hasBeenSeen 
                ? "bounceBallFromBottom 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards, spreadBall 0.6s 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards"
                : "none",
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Loader Content - fades in after spread */}
            {showLoader && (
              <div
                className="flex items-center gap-3"
                style={{
                  paddingInline: 20,
                  animation: "fadeIn 0.3s ease-in",
                }}
              >
                {LOGOS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setIndex(i);
                      setState("playing");
                    }}
                    className={`relative rounded-full transition-all duration-300 ${
                      i === index ? "w-10" : "w-1.5"
                    }`}
                    style={{
                      height: 6,
                      background:
                        i === index
                          ? "rgba(235,235,245,0.85)"
                          : "rgba(235,235,245,0.35)",
                    }}
                    aria-label={`Go to logo ${i + 1}`}
                  >
                    {i === index && (
                      <span
                        className="absolute inset-0 origin-left rounded-full"
                        style={{
                          background: "rgba(235,235,245,0.95)",
                          animation: `fill ${DURATION}ms linear forwards`,
                          animationPlayState:
                            state === "playing" ? "running" : "paused",
                        }}
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Play / Pause Button */}
        {showPlayButton && (
          <button
            onClick={togglePlay}
            className="grid place-items-center rounded-full backdrop-blur-md transition"
            style={{
              height: CONTROL_HEIGHT,
              width: CONTROL_HEIGHT,
              background: "rgba(28,28,30,0.9)",
              color: state === "paused" ? "#3b82f6" : "rgba(235,235,245,0.9)",
              fontSize: 16,
              animation: "fadeIn 0.3s ease-in",
            }}
            aria-label="Gallery control"
          >
            {state === "playing" ? <MdPause size={28} /> : <MdPlayArrow size={28} />}
          </button>
        )}
      </div>

      {/* Progress animation */}
      <style>{`
        @keyframes fill {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        
        @keyframes bounceBallFromBottom {
          0% {
            transform: translateY(120px) scale(0.8);
            opacity: 0;
          }
          60% {
            transform: translateY(-12px) scale(1.05);
            opacity: 1;
          }
          80% {
            transform: translateY(4px) scale(0.98);
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }
        
        @keyframes spreadBall {
          0% {
            width: ${CONTROL_HEIGHT}px;
          }
          100% {
            width: ${LOADER_WIDTH}px;
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}